import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatChip } from '@angular/material/chips';
import { MatSelectChange } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { isEmpty } from 'lodash';
import { of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { ProgramService } from 'src/app/core/api/program.service';
import { RegionService } from 'src/app/core/api/region.service';

import { Program, Region } from '../../../shared/models';
import * as fromApp from '../../../store';

@Component({
  selector: 'fc-edit-program-page',
  templateUrl: './edit-program-page.component.html',
  styleUrls: ['./edit-program-page.component.scss'],
})
export class EditProgramPageComponent implements OnInit {
  destroyRef = inject(DestroyRef);

  id: number = null;
  program: Program = null;
  programForm: UntypedFormGroup;
  error: string = null;
  regionList: Region[];
  regionSelection: string[] = [];
  isSuperAdmin = false;

  constructor(
    readonly programService: ProgramService,
    readonly regionService: RegionService,
    private formBuilder: UntypedFormBuilder,
    readonly router: Router,
    readonly route: ActivatedRoute,
    readonly store: Store<fromApp.AppState>
  ) {
    this.createEditProgramForm();
  }

  ngOnInit() {
    this.id = +this.route.snapshot.params.id;

    // Fetch all regions and set the region list
    this.regionService
      .getAllRegions()
      .pipe(
        tap(allRegions => (this.regionList = allRegions)), // Side effect: Update region list

        // If there's an ID, fetch the program details
        switchMap(allRegions =>
          this.id
            ? this.programService.getProgram(this.id).pipe(
              map(program => ({ allRegions, program })) // Combine allRegions and program into a single object
            )
            : of(null)
        ),

        // Process the combined result of allRegions and program
        tap(result => {
          if (result) {
            const { allRegions, program } = result;
            this.program = program;
            if (this.program) {
              const regionArr = program.regions.map(r => r.id);
              const isNational = this.program.isNational ? 'national' : 'regional';
              this.programForm.get('id').setValue(this.program.id);
              this.programForm.get('name').setValue(this.program.name);
              this.programForm.get('restrictionLevel').setValue(this.program.restrictionLevel);
              this.programForm.get('regions').setValue(regionArr);
              this.programForm.get('isNational').setValue(isNational);
              this.programForm.get('isActive').setValue(this.program.isActive);
              this.regionSelection = regionArr.map((id: number) => allRegions.find(r => r.id === id).name);
              this.changeType({ value: isNational, source: null });
            }
          }
        })
      )
      .subscribe();

    this.programForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(data => {
      this.regionSelection = data.regions
        ? data.regions.map((id: number) => {
          return this.regionList.find(r => r.id === id).name;
        })
        : [];
    });
  }

  createEditProgramForm(): void {
    this.programForm = this.formBuilder.group({
      id: '',
      name: ['', Validators.required],
      restrictionLevel: ['', Validators.required],
      regions: '',
      isNational: ['national', Validators.required],
      isActive: [true, Validators.required],
    });
    this.changeType({ value: 'national', source: null });
  }

  changeType($event: MatSelectChange): void {
    if ($event.value === 'national') {
      this.programForm.get('regions').setValidators([]);
      this.programForm.get('regions').setValue(null);
      this.programForm.get('regions').disable();
    } else {
      this.programForm.get('regions').setValidators(Validators.required);
      this.programForm.get('regions').enable();
    }
  }

  removeRegion($event: { chip: MatChip }): void {
    if (this.programForm && this.regionList && !isEmpty($event.chip.value)) {
      const regionName: string = String($event.chip.value).trim();
      const region = this.regionList.find(s => s.name === regionName);
      if (region) {
        this.programForm.controls.regions.setValue(
          this.programForm.value.regions.filter((id: number) => id !== region.id)
        );
      }
    }
  }

  onSubmit(): void {
    const program: Program = new Program({
      name: this.programForm.value.name,
      restrictionLevel: this.programForm.value.restrictionLevel,
      isNational: this.programForm.value.isNational === 'national',
      isActive: this.programForm.value.isActive,
    });
    if (this.programForm.value.id) {
      program.id = this.programForm.value.id;
    }
    this.programService.saveProgram(program, this.programForm.value.regions).subscribe(() => {
      this.programForm.markAsPristine();
      this.router.navigateByUrl(`/admin/manage-program`);
    });
  }

  get isFormDirty(): boolean {
    return this.programForm.dirty;
  }
}
